import React, { useEffect, useState } from 'react';
import { Form, Input, Button, message, Modal, Space, InputNumber, Popconfirm } from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { dictionariesApi } from '../services/api-services';

const DictionaryManagement: React.FC = () => {
  const [dicts, setDicts] = useState<any[]>([]);
  const [dictModalVisible, setDictModalVisible] = useState(false);
  const [editingDict, setEditingDict] = useState<any>(null);
  const [dictForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const loadDicts = async (page = currentPage, size = pageSize) => {
    try {
      const res: any = await dictionariesApi.list({ page, page_size: size });
      setDicts(res.items);
      setTotal(res.total);
    } catch {}
  };

  useEffect(() => { loadDicts(); }, []);

  const handleSaveDict = async (values: any) => {
    try {
      const items = (values.items || []).map((item: any, idx: number) => ({
        name: item.name,
        value: item.value,
        sort: item.sort ?? idx + 1,
      }));
      const payload = { name: values.name, code: values.code, items };
      if (editingDict) { await dictionariesApi.update(editingDict.id, payload); message.success('更新成功'); }
      else { await dictionariesApi.create(payload); message.success('创建成功'); }
      setDictModalVisible(false); dictForm.resetFields(); setEditingDict(null); loadDicts();
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const handleDeleteDict = async (id: number) => {
    try { await dictionariesApi.delete(id); message.success('删除成功'); loadDicts(); } catch { message.error('删除失败'); }
  };

  const openEdit = (r: any) => {
    setEditingDict(r);
    dictForm.setFieldsValue({ name: r.name, code: r.code, items: r.items || [] });
    setDictModalVisible(true);
  };

  const openCreate = () => {
    setEditingDict(null);
    dictForm.resetFields();
    dictForm.setFieldsValue({ items: [] });
    setDictModalVisible(true);
  };

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name' },
    { title: '编码', dataIndex: 'code', key: 'code' },
    { title: '项数', dataIndex: 'items', key: 'items', render: (items: any[]) => items?.length || 0 },
    { title: '操作', key: 'action', render: (_: any, r: any) => (
      <Space>
        <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
        <Popconfirm title="确定删除该字典？" onConfirm={() => handleDeleteDict(r.id)} okText="删除" okButtonProps={{ danger: true }} cancelText="取消">
          <Button type="link" danger size="small">删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">字典管理</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={openCreate}>新建字典</Button>
      </div>
      <p className="apple-page-subtitle">管理系统级数据字典，供各模块下拉选项使用</p>

      <ResponsiveTable columns={columns} dataSource={dicts} rowKey="id" pagination={{ current: currentPage, pageSize: pageSize, total: total, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t: number) => `共 ${t} 条`, onChange: (page: number, size: number) => { setCurrentPage(page); setPageSize(size); loadDicts(page, size); } }} />

      <Modal
        className="apple-modal"
        title={editingDict ? '编辑字典' : '新建字典'}
        open={dictModalVisible}
        onCancel={() => setDictModalVisible(false)}
        onOk={() => dictForm.submit()}
        width={640}
      >
        <Form className="apple-form" form={dictForm} layout="vertical" onFinish={handleSaveDict}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：性别" />
          </Form.Item>
          <Form.Item name="code" label="编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input placeholder="如：gender" disabled={!!editingDict} />
          </Form.Item>

          <Form.Item label="字典项" required>
            <Form.List name="items" rules={[{ validator: async (_, v) => { if (!v || v.length === 0) return Promise.reject('请至少添加一个字典项'); } }]}>
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 32px', gap: '0 8px', marginBottom: 4, padding: '0 2px' }}>
                      <span style={{ fontSize: 12, color: 'var(--muted-text)' }}>显示名称</span>
                      <span style={{ fontSize: 12, color: 'var(--muted-text)' }}>值</span>
                      <span style={{ fontSize: 12, color: 'var(--muted-text)' }}>排序</span>
                      <span />
                    </div>
                  )}
                  {fields.map(({ key, name }) => (
                    <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 32px', gap: '0 8px', marginBottom: 8, alignItems: 'flex-start' }}>
                      <Form.Item name={[name, 'name']} rules={[{ required: true, message: '必填' }]} style={{ margin: 0 }}>
                        <Input placeholder="如：男" />
                      </Form.Item>
                      <Form.Item name={[name, 'value']} rules={[{ required: true, message: '必填' }]} style={{ margin: 0 }}>
                        <Input placeholder="如：male" />
                      </Form.Item>
                      <Form.Item name={[name, 'sort']} style={{ margin: 0 }}>
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="排序" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        style={{ marginTop: 1 }}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ sort: fields.length + 1 })}
                    icon={<PlusOutlined />}
                    block
                  >
                    添加字典项
                  </Button>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DictionaryManagement;
